import { useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { updateFormField } from 'store/forms/actions';
import { steps } from '../../steps';
import styles from './ViewFormFooter.module.scss';

/**
 * @component
 * @description
 * **MainForm footer** component for the multi-tab and sub-tab form.
 * Handles navigation between main tabs and sub-tabs.
 * SubTab changed is handled using redux.
 * @param {Object} props - Component props object.
 * @param {number} props.currentTab - The currently active main tab index.
 * @param {Function} props.setCurrentTab - Setter that updates the active main tab.
 * @returns {JSX.Element} The rendered footer with navigation controls.
 */
export function ViewFormFooter({ currentTab, setCurrentTab }) {
  const navigate = useNavigate();
  const dispatch = useDispatch();

  const currentSubTab =
    useSelector(state => state.forms.carrier.currentSubTab) || 0;

  const tabMap = useMemo(() => buildTabMap(), []);
  const mainTabs = useMemo(() => Object.keys(tabMap).map(Number), [tabMap]);

  const getSubTabs = tabId => tabMap[tabId] || null;

  const setCurrentSubTab = value => {
    dispatch(
      updateFormField({
        group: 'carrier',
        key: 'currentSubTab',
        value,
      })
    );
  };

  const subTabs = getSubTabs(currentTab);
  const currentMainTabIndex = mainTabs.indexOf(currentTab);
  const currentSubTabIndex = subTabs ? subTabs.indexOf(currentSubTab) : -1;

  const hasNextSubTab = subTabs && currentSubTabIndex < subTabs.length - 1;
  const hasNextMainTab = currentMainTabIndex < mainTabs.length - 1;
  const hasNextTab = hasNextSubTab || hasNextMainTab;

  const hasPrevSubTab = subTabs && currentSubTabIndex > 0;
  const hasPrevMainTab = currentMainTabIndex > 0;
  const hasPreviousTab = hasPrevSubTab || hasPrevMainTab;

  const goNext = () => {
    if (hasNextSubTab) {
      setCurrentSubTab(subTabs[currentSubTabIndex + 1]);
      return;
    }

    if (hasNextMainTab) {
      const nextTab = mainTabs[currentMainTabIndex + 1];
      setCurrentTab(nextTab);
      const nextSubTabs = getSubTabs(nextTab);
      setCurrentSubTab(nextSubTabs ? nextSubTabs[0] : null);
    }
  };

  const goPrev = () => {
    if (hasPrevSubTab) {
      setCurrentSubTab(subTabs[currentSubTabIndex - 1]);
      return;
    }

    if (hasPrevMainTab) {
      const prevTab = mainTabs[currentMainTabIndex - 1];
      setCurrentTab(prevTab);
      const prevSubTabs = getSubTabs(prevTab);
      setCurrentSubTab(
        prevSubTabs ? prevSubTabs[prevSubTabs.length - 1] : null
      );
    }
  };

  return (
    <div className={styles.formsFooterContainer}>
      <div className={styles.formsFooter}>
        <div className={styles.formsFooterLeft}>
          <button
            className={`${styles.btn} ${styles.purpleBtn}`}
            onClick={() => {
              if (hasPreviousTab) {
                goPrev();
              } else {
                navigate('/appTrack');
              }
            }}
          >
            {hasPreviousTab ? '← Προηγούμενο' : '← Πίσω στην My Plans'}
          </button>
          {hasNextTab && (
            <button
              className={`${styles.btn} ${styles.purpleBtn}`}
              disabled={!hasNextTab}
              onClick={goNext}
            >
              Επόμενο →
            </button>
          )}
        </div>
        <div className={styles.formsFooterRight}></div>
      </div>
    </div>
  );
}

/**
 * @description
 * Builds a mapping between main tabs and their inner sub-tabs.
 * The result is an object where each key is a main tab index
 * and each value is either:
 *   - an array of sub-tab indexes (e.g. [0, 1, 2]), or
 *   - null if that main tab has no sub-tabs.
 *
 * @example
 * {
 *   0: [0, 1, 2],
 *   1: null,
 *   2: [0, 1, 2, 3],
 *   3: null,
 *   4: null
 * }
 *
 * @param {boolean} [parseFlag] - Optional parameter passed into Steps() (if required by the business logic).
 * @returns {Object.<number, number[] | null>} A mapping of main tab → list of sub-tabs.
 */
const buildTabMap = () => {
  const stepList = steps;
  const map = {};

  stepList.forEach(step => {
    map[step.id - 1] = null;
  });

  return map;
};
